import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  sendEmailCode as sendEmailCodeAPI,
  mergeIdentities as mergeIdentitiesAPI,
  submitManualApplication as submitManualApplicationAPI,
  checkEmail as checkEmailAPI,
  sendUpgradeSmsCode as sendUpgradeSmsCodeAPI,
  register as registerAPI,
  type MergeSmsVerification,
} from '../../api/register';
import { calcPasswordStrength } from '../../utils/passwordRules';
import type {
  RegisterFormData,
  RegisterStep,
  RegisterState,
  RegisterActions,
  RegisterErrorFlags,
} from './types';
import { initialErrors, initialState, initialFormData, saveToLocalStorage } from './initialState';
import { getFieldValueError } from './validators';

export * from './types';

// 创建store
export const useRegisterStore = create<RegisterState & RegisterActions>()(
  devtools(
    (set, get) => ({
      ...initialState,

      // 步骤控制
      setStep: (step) => set({ currentStep: step }),

      nextStep: () => {
        const { currentStep } = get();
        if (currentStep < 5) {
          set({ currentStep: (currentStep + 1) as RegisterStep });
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 1) {
          set({ currentStep: (currentStep - 1) as RegisterStep });
          // 从检测步返回信息步：重置检测结果
          if (currentStep === 3) {
            set({ systemCheckResult: null });
          }
        }
      },

      // 表单操作
      setFormField: (field, value) => {
        set((state) => {
          const newFormData = { ...state.formData, [field]: value };
          // 换绑邮箱：旧邮箱的验证码/占用结论/发送目标全部作废，
          // 必须对新邮箱重新发送验证码完成验证（防止旧结果冒充新邮箱通过，直到提交才被后端拦截）
          if (field === 'email' && value !== state.formData.email) {
            saveToLocalStorage({ ...newFormData, emailCode: '' });
            return {
              formData: { ...newFormData, emailCode: '' },
              emailStatus: 'idle' as const,
              emailCodeSentTo: '',
              emailCodeCountdown: 0,
              emailCodeSending: false,
            };
          }
          saveToLocalStorage(newFormData);
          return {
            formData: newFormData
          };
        });
      },

      setError: (field, error) => {
        set((state) => ({
          errors: { ...state.errors, [field]: error }
        }));
      },

      clearError: (field) => {
        set((state) => ({
          errors: { ...state.errors, [field]: '' }
        }));
      },

      clearAllErrors: () => set({ errors: { ...initialErrors } }),

      resetForm: () => {
        localStorage.removeItem('registerFormData');
        set({ formData: { ...initialFormData } });
      },

      // UI状态操作

      setAgreed: (value) => set({ agreed: value }),
      setShowPassword: (value) => set({ showPassword: value }),
      setShowConfirmPassword: (value) => set({ showConfirmPassword: value }),

      // 注册流程状态操作
      setRegisterCountdown: (value) => set({ registerCountdown: value }),

      decrementCountdown: () => {
        set((state) => ({
          registerCountdown: Math.max(0, state.registerCountdown - 1)
        }));
      },

      setHasReadToBottom: (value) => set({ hasReadToBottom: value }),
      setUsernameStatus: (status) => set({ usernameStatus: status }),
      setEmailStatus: (status) => set({ emailStatus: status }),
      setPhoneStatus: (status) => set({ phoneStatus: status }),
      setBackupPhoneStatus: (status) => set({ backupPhoneStatus: status }),
      setIdCardStatus: (status) => set({ idCardStatus: status }),
      setPasswordStrength: (strength) => set({ passwordStrength: strength }),

      calculatePasswordStrength: (password) => {
        // 复用全局密码规则（与后端、重置密码页保持一致）
        set({ passwordStrength: calcPasswordStrength(password) });
      },

      // 邮箱验证码操作
      sendEmailCode: async () => {
        const { formData, emailCodeSending, emailCodeCountdown, emailStatus } = get();
        if (emailCodeSending || emailCodeCountdown > 0) return;
        const email = formData.email.trim();
        if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          set((state) => ({ errors: { ...state.errors, email: '请先输入正确的邮箱地址' } }));
          return;
        }
        if (emailStatus === 'taken') return;
        // 时序修复：检查尚未确认可用时，先强制检查一次，防止防抖检查未返回就发送
        if (emailStatus !== 'ok' && emailStatus !== 'customer') {
          const resp = await checkEmailAPI(email);
          if (resp.status === 'user') {
            set((state) => ({
              emailStatus: 'taken',
              errors: { ...state.errors, email: resp.message || '已注册，请登录' },
            }));
            return;
          }
          if (resp.status === 'customer') {
            set({ emailStatus: 'customer' });
          } else if (resp.status === 'available') {
            set({ emailStatus: 'ok' });
          }
        }

        set({ emailCodeSending: true });
        try {
          await sendEmailCodeAPI(email, formData.fullName.trim() || undefined);
          set({
            emailCodeSending: false,
            emailCodeCountdown: 60,
            emailCodeSentTo: email,
            formData: { ...get().formData, emailCode: '' }
          });
        } catch (error) {
          set({ emailCodeSending: false });
          throw error;
        }
      },

      decrementEmailCodeCountdown: () => {
        set((state) => ({ emailCodeCountdown: Math.max(0, state.emailCodeCountdown - 1) }));
      },

      sendUpgradeSmsCode: async () => {
        const { formData, smsCodeSending, smsCodeCountdown } = get();
        if (smsCodeSending || smsCodeCountdown > 0) return;
        const phone = formData.phone.trim();
        if (!/^1[3-9]\d{9}$/.test(phone)) {
          set((state) => ({ errors: { ...state.errors, phone: '请输入正确的手机号' } }));
          return;
        }
        set({ smsCodeSending: true });
        try {
          await sendUpgradeSmsCodeAPI(phone);
          set({ smsCodeSending: false, smsCodeCountdown: 60 });
        } catch (error) {
          set({ smsCodeSending: false });
          throw error;
        }
      },

      decrementSmsCodeCountdown: () => {
        set((state) => ({ smsCodeCountdown: Math.max(0, state.smsCodeCountdown - 1) }));
      },

      // 身份验证操作
      setIdentityVerificationLoading: (loading) => {
        set((state) => ({
          identityVerification: { ...state.identityVerification, isLoading: loading }
        }));
      },

      setIdentityVerified: (verified) => {
        set((state) => ({
          identityVerification: { ...state.identityVerification, isVerified: verified }
        }));
      },

      setIdentityVerificationError: (error) => {
        set((state) => ({
          identityVerification: { ...state.identityVerification, error }
        }));
      },


      resetIdentityVerification: () => {
        set({
          identityVerification: {
            isLoading: false,
            isVerified: false,
            error: null
          }
        });
      },

      // 系统检测操作
      setSystemCheckLoading: (loading) => set({ systemCheckLoading: loading }),
      setSystemCheckResult: (result) => set({ systemCheckResult: result }),

      // 注册结果操作

      setRegisterSuccessData: (data) => set({ registerSuccessData: data }),

      // 提交注册（五步制最后一步：信息步已完成三要素验证与邮箱码验证）
      // disputeToken：认领弹窗「非本人信息」申诉凭证（验证通过仅持有，提交时后端才腾位/停用）
      submitRegister: async (releaseToken?: string, disputeToken?: string) => {
        const { formData, systemCheckResult, selectedPrimaryIdentity, setRegisterSuccessData } = get();

        try {
          const data = await registerAPI({
            fullName: formData.fullName,

            phone: formData.phone,
            backupPhone: formData.backupPhone || undefined,
            idCard: formData.idCard,
            username: formData.username,
            password: formData.password,
            email: formData.email,
            emailCode: formData.emailCode,
            smsCode: formData.smsCode || undefined,
            customerId: systemCheckResult?.matchedEntity?.id,
            primaryIdentityId: selectedPrimaryIdentity || undefined,
            releaseToken: disputeToken || releaseToken || undefined,
            disputeRelease: disputeToken || undefined,
          });


          setRegisterSuccessData(data);
          return { ok: true };
        } catch (error) {
          const flags: RegisterErrorFlags = (error as { data?: RegisterErrorFlags })?.data ?? {};
          console.error('[submitRegister] 注册失败:', flags, error?.message);
          return { ok: false, errorFlags: flags, message: (error as { message?: string })?.message || '注册失败，请稍后重试' };
        }
      },

      // 人工申请操作
      setShowManualApplication: (show) => set({ showManualApplication: show }),
      setManualApplicationReason: (reason) => set({ manualApplicationReason: reason }),
      setManualApplicationPhoto: (photo) => set({ manualApplicationPhoto: photo }),

      submitManualApplication: async () => {
        const { formData, manualApplicationReason, manualApplicationPhoto } = get();

        try {
          await submitManualApplicationAPI({
            username: formData.username || undefined,
            fullName: formData.fullName,
            phone: formData.phone,
            idCard: formData.idCard,
            backupPhone: formData.backupPhone || undefined,
            email: formData.email,
            reason: manualApplicationReason,
            idCardPhoto: manualApplicationPhoto || undefined
          });
          set({ showManualApplication: false });
        } catch (error) {
          console.error('提交人工申请失败:', error);
          throw error;
        }
      },

      // 身份合并操作
      setShowIdentityMerge: (show) => set({ showIdentityMerge: show }),
      setSelectedPrimaryIdentity: (id) => set({ selectedPrimaryIdentity: id }),

      mergeIdentities: async (verifications?: MergeSmsVerification[]) => {
        const { selectedPrimaryIdentity, systemCheckResult, formData } = get();
        if (!selectedPrimaryIdentity || !systemCheckResult?.matchedEntities) {
          throw new Error('未选择主身份');
        }

        try {
          await mergeIdentitiesAPI({
            primaryId: selectedPrimaryIdentity,
            entityIds: systemCheckResult.matchedEntities
              .filter(e => e.id !== selectedPrimaryIdentity)
              .map(e => e.id),
            // 后端要求 register_data 不能为空；透传当前表单数据用于按注册信息覆盖主档字段
            registerData: {
              fullName: formData.fullName,
              phone: formData.phone,
              backupPhone: formData.backupPhone,
              idCard: formData.idCard,
              email: formData.email,
              username: formData.username,
              password: formData.password,
            },
            // 仅号码命中次档的短信验证（无需要验证的次档时为空数组）
            verifications,
          });

          // 合并成功后更新状态
          set({
            showIdentityMerge: false,
            systemCheckResult: {
              ...systemCheckResult,
              multipleIdentities: false,
              matchedEntity: systemCheckResult.matchedEntities?.find(e => e.id === selectedPrimaryIdentity)
            }
          });
        } catch (error) {
          console.error('身份合并失败:', error);
          throw error;
        }
      },

      // 客户绑定操作
      setShowCustomerBinding: (show) => set({ showCustomerBinding: show }),


      // 「非本人信息」处理弹窗
      setShowMismatchResolution: (show) => set({ showMismatchResolution: show }),


      // 验证操作
      validateField: (field) => {
        const { formData, setError, clearError } = get();
        const error = getFieldValueError(field, formData[field], formData);

        if (error) {
          setError(field, error);
          return false;
        } else {
          clearError(field);
          return true;
        }
      },

      // 信息+验证步：全部字段格式 + 邮箱验证码
      validateStep2: () => {
        const { validateField } = get();
        const fields: (keyof RegisterFormData)[] = ['username', 'fullName', 'phone', 'backupPhone', 'idCard', 'email', 'emailCode'];
        let isValid = true;

        fields.forEach(field => {
          if (!validateField(field)) {
            isValid = false;
          }
        });

        return isValid;
      },

      // 密码步
      validateStep4: () => {
        const { validateField } = get();
        const fields: (keyof RegisterFormData)[] = ['password', 'confirmPassword'];
        let isValid = true;

        fields.forEach(field => {
          if (!validateField(field)) {
            isValid = false;
          }
        });

        return isValid;
      },

      // 重置整个store
      resetStore: () => {
        localStorage.removeItem('registerFormData');
        set({ ...initialState });
      }
    }),
    { name: 'RegisterStore' }
  )
);